import { EMPTY, expand, type Observable, reduce } from 'rxjs';

/**
 * Largest page the API serves. Mirrors MAX_PAGE_LIMIT in libs/src/dtos/shared.dto.ts (not imported:
 * that module evaluates Node-only code such as `Buffer` at load time).
 */
export const API_MAX_PAGE_LIMIT = 100;

/** Shape shared by every paginated list response of the API (some omit totalPages). */
export interface PagedResponse<T> {
	data: T[];
	total: number;
	page: number;
	limit: number;
	totalPages?: number;
}

/**
 * Walks every page of a paginated endpoint with the largest page size and emits once, with all the rows
 * in a single response (as page 1 of 1). Without it a list read silently stops at the API's
 * default page size (10).
 *
 * @param fetchPage requests one page with the given `page` and `limit` query params
 */
export function fetchAllPages<R extends PagedResponse<unknown>>(
	fetchPage: (paging: { page: number; limit: number }) => Observable<R>,
): Observable<R> {
	const limit = API_MAX_PAGE_LIMIT;
	return fetchPage({ page: 1, limit }).pipe(
		expand((response) => {
			const fetched = response.page * limit;
			const hasMore = (response.data?.length ?? 0) === limit && fetched < (response.total ?? 0);
			return hasMore ? fetchPage({ page: response.page + 1, limit }) : EMPTY;
		}),
		reduce(
			(all, response) =>
				({
					...response,
					data: [...(all?.data ?? []), ...(response.data ?? [])],
					page: 1,
					...('totalPages' in response ? { totalPages: 1 } : {}),
				}) as R,
			null as R | null,
		),
		// reduce seeded with null always emits a value once the source completes
	) as Observable<R>;
}
